import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import '../styles/RoomLaunch.css';


const RoomLaunchSidebar = ({ user }) => {
  const [rooms, setRooms] = useState({
    pomona: [],
    lakewood: [],
    israel: [],
    china: []
  });
  const [selectedLocation, setSelectedLocation] = useState('pomona'); // Default to Pomona
  const [selectedRoom, setSelectedRoom] = useState('');
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);

  const locations = [
    { key: 'pomona', label: 'Pomona' },
    { key: 'lakewood', label: 'Lakewood' },
    { key: 'israel', label: 'Israel' },
    { key: 'china', label: 'China' }
  ];

  // Fetch rooms data on component mount
  useEffect(() => {
    fetchRooms();
    // console.log(rooms)
  }, []);

  // Reset selected room when location changes
  useEffect(() => {
    if (selectedLocation) {
      const locationRooms = rooms[selectedLocation] || [];
      if (locationRooms.length > 0) {
        setSelectedRoom(locationRooms[0].RoomNo);
      } else {
        setSelectedRoom('');
      }
    }
  }, [selectedLocation, rooms]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      
      const locationNames = ['Pomona', 'Lakewood', 'Israel', 'China'];
      const roomPromises = locationNames.map(location => 
        fetch(`${API_URL}/api/get-rooms/${location}`)
          .then(response => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .catch(error => {
            console.error(`Error fetching ${location} rooms:`, error);
            return [];
          })
      );

      const [pomonaRooms, lakewoodRooms, israelRooms, chinaRooms] = await Promise.all(roomPromises);

      setRooms({
        pomona: pomonaRooms || [],
        lakewood: lakewoodRooms || [],
        israel: israelRooms || [],
        china: chinaRooms || []
      });

      // Set default room for Pomona since it's the default location
      if (pomonaRooms && pomonaRooms.length > 0) {
        setSelectedRoom(pomonaRooms[0].RoomNo);
      }

    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (location) => {
    setSelectedLocation(location);
  };

  const handleRoomChange = (roomNo) => {
    setSelectedRoom(roomNo);
  };

  const handleLaunchRoom = async () => {
    if (!selectedLocation || !selectedRoom) {
      alert('Please select both a location and room to launch');
      return;
    }

    try {
      setLaunching(true);

      // Navigate to manufacturing with room number using your route pattern
      const roomUrl = `/manufacturing/room/${selectedRoom}`;
      
      // Open in new tab
      window.open(roomUrl, '_blank');
      
      console.log(`Launching ${selectedLocation} room ${selectedRoom}`);
      
    } catch (error) {
      console.error('Error launching room:', error);
      alert('Failed to launch room');
    } finally {
      setLaunching(false);
    }
  };

  const getCurrentRooms = () => {
    return selectedLocation ? rooms[selectedLocation] || [] : [];
  };

  if (loading) {
    return (
      <div className="room-launch" aria-busy="true" aria-label="Loading rooms">
        <span className="room-launch-skeleton room-launch-skeleton--location" />
        <span className="room-launch-skeleton room-launch-skeleton--select" />
        <span className="room-launch-skeleton room-launch-skeleton--btn" />
      </div>
    );
  }

  const currentRooms = getCurrentRooms();
  const selectedRoomDetails = currentRooms.find(r => String(r.RoomNo) === String(selectedRoom));
  const selectedLocationLabel = locations.find(loc => loc.key === selectedLocation)?.label;

  return (
    <div className="room-launch">
      {/* Location selection */}
      <select
        className="room-launch-select room-launch-select--location"
        value={selectedLocation}
        onChange={(e) => handleLocationChange(e.target.value)}
        aria-label="Location"
      >
        {locations.map(location => (
          <option key={location.key} value={location.key}>
            {location.label}
          </option>
        ))}
      </select>

      {/* Room selection */}
      <select
        className="room-launch-select room-launch-select--room"
        value={selectedRoom}
        onChange={(e) => handleRoomChange(e.target.value)}
        aria-label={`Room in ${selectedLocationLabel}`}
        title={selectedRoomDetails ? `${selectedRoomDetails.RoomNo} · ${selectedRoomDetails.RoomDescription}` : undefined}
        disabled={currentRooms.length === 0}
      >
        {currentRooms.length === 0 && <option value="">No rooms</option>}
        {currentRooms.map(room => (
          <option key={room.RoomNo} value={room.RoomNo}>
            {room.RoomNo} · {room.RoomDescription}
          </option>
        ))}
      </select>

      {/* Launch */}
      <button
        type="button"
        className="room-launch-btn"
        aria-label="Launch room"
        onClick={handleLaunchRoom}
        disabled={launching || !selectedLocation || !selectedRoom}
        title={selectedRoom ? `Open Room ${selectedRoom} in a new tab` : 'Select a room to launch'}
      >
        <i className="fas fa-rocket" aria-hidden="true"></i>
        <span>Launch</span>
      </button>
    </div>
  );
};

export default RoomLaunchSidebar;